import type { Task } from '@dyspatch/core';
import { Link } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { Age, RepoChip, TaskKey } from '@/components/common/bits';
import { Section, SectionHead, Surface } from '@/components/ui/section';
import { FailureActions } from '@/features/actions/GateActions';
import { failures24h, olderFailures } from '@/lib/fleet';
import { minutesSince } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * Failures in the last 24 hours, and where the older ones went.
 *
 * It renders nothing at all when there are no failures of either kind: an empty
 * "Failures" region is a claim that failure is a normal part of the page, and it
 * is not. But a fleet whose last failure was two days ago has failures nobody has
 * decided anything about, and hiding the region there hides the only pointer to
 * them, so an older-only fleet gets the heading and the footer without the list.
 *
 * It moved off the home page with the drafts, and for the same reason: a failed
 * step is not a gate. It is a job that went wrong, and a job's life is this page.
 */
export function Failures({ tasks, capped }: { tasks: Task[]; capped: boolean }) {
  const failed = failures24h(tasks);
  const older = olderFailures(tasks).length;
  const [open, setOpen] = useState<string | null>(null);
  if (!failed.length && !older) return null;

  return (
    <Section>
      <SectionHead
        title="Failures"
        hue="var(--dy-bad)"
        meta={
          failed.length
            ? `last 24 hours · ${failed.length}`
            : capped
              ? 'older than 24 hours'
              : `older than 24 hours · ${older}`
        }
      />
      <Surface>
        <ul className="divide-line divide-y">
          {failed.map((task) => (
            <li
              key={task.id}
              className={cn(
                'transition-colors duration-100',
                open === task.id ? 'bg-bad-bg/25' : 'hover:bg-raised/60',
              )}
            >
              {/* Stretched toggle, as on a gate row: same shape, same reason. A
                  14px chevron is not a target anybody hits one-handed. */}
              <div className="relative flex items-start gap-3 px-gutter py-3 max-sm:min-h-11">
                <button
                  type="button"
                  onClick={() => setOpen((previous) => (previous === task.id ? null : task.id))}
                  aria-expanded={open === task.id}
                  aria-label={open === task.id ? `Collapse ${task.title}` : `Open ${task.title}`}
                  title={task.title}
                  className="absolute inset-0"
                />
                <ChevronRight
                  className={cn(
                    'text-fg-3 pointer-events-none mt-1 size-4 shrink-0 transition-transform duration-150',
                    open === task.id && 'rotate-90',
                  )}
                  aria-hidden
                />
                <div className="pointer-events-none relative min-w-0 flex-1 [&_a]:pointer-events-auto">
                  <div className="flex min-w-0 items-center gap-2.5">
                    <TaskKey briefId={task.briefId}>{task.key}</TaskKey>
                    <Link
                      to="/tasks/$taskId"
                      params={{ taskId: task.id }}
                      className="hover:text-gate min-w-0 truncate text-base transition-colors duration-100"
                    >
                      {task.title}
                    </Link>
                  </div>
                  <div className="mt-1 flex min-w-0 items-center gap-2.5">
                    <RepoChip repo={task.repo} />
                    {task.assignedWorkerName ? (
                      <span className="text-fg-3 shrink-0 text-xs">{task.assignedWorkerName}</span>
                    ) : null}
                    {/* The clipped preview is what the row has instead of the
                        error; once the row is open the whole thing is directly
                        below it, and printing both is the same paragraph twice. */}
                    {open === task.id ? null : (
                      <span
                        className="text-fg-3 min-w-0 truncate text-xs"
                        title={task.lastError ?? ''}
                      >
                        {task.lastError?.slice(0, 120)}
                      </span>
                    )}
                  </div>
                </div>
                <span className="relative mt-0.5 shrink-0">
                  <Age minutes={minutesSince(task.updatedAt)} />
                </span>
              </div>
              {open === task.id ? (
                <div className="sm:pl-12">
                  {task.lastError ? (
                    <p className="text-fg-2 px-gutter pb-1 text-sm whitespace-pre-wrap">
                      {task.lastError}
                    </p>
                  ) : null}
                  <FailureActions
                    taskId={task.id}
                    attempts={task.attempts}
                    maxAttempts={task.maxAttempts}
                  />
                </div>
              ) : null}
            </li>
          ))}
        </ul>
        {/* Where the older ones went. The 24 hour window is deliberate - a failure
            from last Tuesday is not what this morning is about - but it used to be
            the end of the trail, and a failure nobody decided anything about
            should never be a thing the console has quietly stopped mentioning. */}
        {older ? (
          <div className={cn('py-2.5 text-center', failed.length && 'border-line border-t')}>
            <Link
              to="/tasks"
              search={{ status: 'failed', range: '30d' }}
              className="text-fg-3 hover:text-bad text-xs transition-colors duration-100"
            >
              {/* The count comes from a list the hub caps and returns oldest
                  first, so a full page means there may be failures it never sent
                  and any number printed here would be a guess. The link still
                  works; only the arithmetic goes away. */}
              {capped ? 'Older failures' : `${older} older failure${older === 1 ? '' : 's'}`} →
            </Link>
          </div>
        ) : null}
      </Surface>
    </Section>
  );
}
